import { limit, orderBy, where } from "firebase/firestore";
import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router";

import {
    useLocationCollection,
    useResultCollectionGroup,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import LoadMoreButton from "../../components/LoadMoreButton/index.jsx";
import { DateFilter } from "../../components/MissionFilters/index.jsx";
import ResultsList, {
    hasPublishedFiles,
} from "../../components/ResultsList/index.jsx";
import { useUser } from "../../contexts/user_provider.jsx";
import urls from "../../urls.jsx";

export default function ResultListPage() {
    const resultsPerPage = 50;

    const { organizationId, locationId } = useParams();
    const location = useLocation();
    const queryParams = queryString.parse(location.search);
    const navigate = useNavigate();
    const { getUserIsAdmin } = useUser();
    const isAdmin = useMemo(() => getUserIsAdmin(), [getUserIsAdmin]);
    const [locationMap] = useLocationCollection(
        organizationId,
        [orderBy("name", "asc")],
        { type: "map" },
    );
    const hasMultipleLocations =
        Object.values(locationMap || {}).filter((loc) => loc?.deleted !== true)
            .length > 1;

    useEffect(() => {
        if (!locationId || locationId === "undefined") {
            if (organizationId && organizationId !== "undefined") {
                navigate(urls.locations(organizationId), { replace: true });
            } else {
                navigate(urls.home(), { replace: true });
            }
        }
    }, [locationId, organizationId, navigate]);

    const [filters, setFilters] = React.useState({
        startDate: queryParams.startDate
            ? new Date(Number(queryParams.startDate))
            : undefined,

        endDate: queryParams.endDate
            ? new Date(Number(queryParams.endDate))
            : undefined,

        hidePublished: queryParams.hidePublished === "true",
        showAllLocations: queryParams.showAllLocations === "true",
    });

    const [resultsToLoad, setResultsToLoad] = React.useState(resultsPerPage);
    const [results, isLoading] = useResultCollectionGroup([
        where("organizationId", "==", organizationId),
        ...(filters.showAllLocations
            ? []
            : [where("locationId", "==", locationId)]),
        where("hasFiles", "==", true),
        ...(filters?.startDate
            ? [where("timestamp", ">=", filters?.startDate)]
            : []),
        ...(filters?.endDate
            ? [where("timestamp", "<=", filters?.endDate)]
            : []),
        orderBy("timestamp", "desc"),
        limit(resultsToLoad),
    ]);

    React.useEffect(() => {
        if (
            !organizationId ||
            !locationId ||
            organizationId === "undefined" ||
            locationId === "undefined"
        )
            return;

        const newQuery = {
            startDate: filters.startDate
                ? filters.startDate.getTime()
                : undefined,
            endDate: filters.endDate ? filters.endDate.getTime() : undefined,
            hidePublished: filters.hidePublished ? "true" : undefined,
            showAllLocations: filters.showAllLocations ? "true" : undefined,
        };

        // Remove undefined keys to clean up the URL
        const cleanedQuery = Object.fromEntries(
            Object.entries(newQuery).filter(([, v]) => v !== undefined),
        );

        const newSearch = queryString.stringify(cleanedQuery);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }, [filters, location.pathname, navigate, organizationId, locationId]);

    const seenHashes = new Set();

    const resultsByDateAndApp = results.reduce((acc, result) => {
        const originalFiles = result.files ?? [];

        // Deduplicate by hash (latest wins)
        const dedupedFiles = originalFiles.filter((file) => {
            if (!file.hash) return false;

            if (seenHashes.has(file.hash)) {
                return false;
            }

            seenHashes.add(file.hash);
            return true;
        });

        const files =
            isAdmin && filters.hidePublished
                ? dedupedFiles.filter((file) => !file.published)
                : dedupedFiles;

        // Skip results with no files
        if (files.length === 0) {
            return acc;
        }

        // Skip results with no published files
        if (!hasPublishedFiles(files, isAdmin)) {
            return acc;
        }

        const date = moment(result.timestamp.toDate()).format("YYYY-MM-DD");
        const appId = result.app.id;

        acc[date] ??= {};
        acc[date][appId] ??= [];
        acc[date][appId].push({
            ...result,
            files,
        });

        return acc;
    }, {});

    return (
        <Layout>
            <title>Results | MyCorvus</title>
            <Breadcrumbs path={breadcrumbPaths.results} />
            <Card className="mb-3">
                <Title
                    title={"Results"}
                    backButtonUrl={urls.location(organizationId, locationId)}
                />
            </Card>
            {isAdmin && (
                <Row className="mb-2">
                    <Col xs={12} md={4}>
                        <Form.Check
                            type="switch"
                            id="hide-published-switch"
                            label="Hide published results"
                            checked={filters.hidePublished ?? false}
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    hidePublished: e.target.checked,
                                }))
                            }
                        />
                    </Col>
                </Row>
            )}
            {hasMultipleLocations && (
                <Row className="mb-2">
                    <Col>
                        <Form.Check
                            type="switch"
                            id="show-all-locations"
                            label="Show results from all locations"
                            checked={filters.showAllLocations ?? false}
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    showAllLocations: e.target.checked,
                                }))
                            }
                        />
                    </Col>
                </Row>
            )}
            <Row>
                <Col xs={12} md={4}>
                    <DateFilter
                        className={"mb-2"}
                        filters={filters}
                        setFilters={setFilters}
                    />
                </Col>
            </Row>

            <ResultsList
                resultsByDateAndApp={resultsByDateAndApp}
                isAdmin={isAdmin}
                showLocationName={hasMultipleLocations}
                locationMap={locationMap}
            />

            <LoadMoreButton
                disabled={resultsToLoad > results.length}
                isLoading={isLoading}
                setIsLoading={() => {}}
                load={() => {
                    setResultsToLoad(resultsToLoad + resultsPerPage);
                }}
                showLoadAll={false}
                loadAll={() => {}}
            />
        </Layout>
    );
}
